/**
 * Cart confirmation modal (feature 7) — global. Replaces the native cart drawer on add: on the
 * native `cart:add` (which Horizon fires as `cart:update` with source `product-form-component`), fetch
 * the added variant's modal via the Section Rendering API and open it. Fetching a product-scoped
 * section gives full Liquid access (colour metaobject, écru packshot, price/compare, cart.item_count)
 * that the cart AJAX JSON can't provide. Built on the native `dialog-component` (loaded globally in
 * the layout), so focus-trap / Esc / click-outside / scroll-lock are all native.
 */
(() => {
  const ADD_SOURCE = 'product-form-component';
  const SECTION_ID = 'jd-cart-modal';
  let root;

  const getRoot = () => {
    if (!root) root = document.querySelector('[data-jd-cart-modal-root]');
    return root;
  };

  // Current on-screen bottom of the sticky header, so the blur veil starts exactly at its edge. The
  // announcement bar is static (scrolls away) while the header row is sticky, so the header height
  // differs between the top of the page and a scrolled position — --header-group-height (static full
  // height) only fits the former. Measure the header-group's direct children still pinned at the top.
  const headerBottom = () => {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return 0;
    let bottom = 0;
    for (const child of headerGroup.children) {
      const rect = child.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < 200) bottom = Math.max(bottom, rect.bottom);
    }
    return bottom;
  };

  const openModal = async (handle, variantId) => {
    const container = getRoot();
    if (!container || !handle) return;

    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let url = `${base}products/${handle}?section_id=${SECTION_ID}`;
    if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;

    let html;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      html = await response.text();
    } catch (error) {
      return;
    }

    container.innerHTML = html;
    const host = container.querySelector('dialog-component');
    if (!host) return;

    // Pin the veil to the live header edge (overrides the CSS --header-group-height fallback).
    const veil = container.querySelector('.jd-cart-modal__veil');
    if (veil) veil.style.insetBlockStart = `${headerBottom()}px`;

    customElements.whenDefined('dialog-component').then(() => {
      if (typeof host.showDialog === 'function') {
        host.showDialog();
      } else {
        const dialog = host.querySelector('dialog');
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      }
    });
  };

  // Which surface confirms an add. The PDP's own buy buttons open the modal; every other add — the
  // quick-add of a product card, wherever it sits, including on a PDP and inside the drawer itself —
  // opens the cart drawer. The decision is made on the trigger, not on the page type.
  //
  // The event bubbles from the product-form-component that dispatched it, so `target` identifies the
  // trigger. `detail.sourceId` cannot: product-form.js fills it with the element id on one path and
  // with the variant id on another (`formData.get('id')`).
  const opensDrawer = (target) => {
    return !(target instanceof Element) || !target.closest('.buy-buttons-block');
  };

  const openDrawer = () => {
    const drawer = document.getElementById('jd-cart-drawer');
    if (!drawer) return;
    customElements.whenDefined('cart-drawer-component').then(() => {
      if (typeof drawer.open === 'function') drawer.open();
    });
  };

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail;
    const data = detail && detail.data;
    // Only genuine adds (not cart-page quantity edits, not errors).
    if (!data || data.source !== ADD_SOURCE || data.didError) return;

    if (opensDrawer(event.target)) {
      openDrawer();
      return;
    }

    // The modal is the confirmation now, so revert the sticky bar's "added" checkmark back to the
    // default "add to cart" label (its native reset relies on a fly-to-cart animationend that our
    // modal interrupts, leaving the checkmark stuck).
    document
      .querySelectorAll('sticky-add-to-cart [data-added]')
      .forEach((button) => button.removeAttribute('data-added'));

    const variantId = data.variantId || detail.sourceId;
    const items = (detail.resource && detail.resource.items) || [];
    const added = items.find((item) => String(item.variant_id) === String(variantId));
    const handle = (added && added.handle) || (detail.resource && detail.resource.handle);
    if (!handle) return;

    openModal(handle, variantId);
  });
})();
